import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TICKET_TITLE_CHANGED_EVENT } from '../utils/ticketTitleEvents';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ChatSidebar from './ChatSidebar';
import MobileAppNav from './MobileAppNav';
import { listPartnerRepairRequests } from '../utils/partnerShopApi';
import {
    getApiBaseUrl,
    getUserDetailsBaseUrl,
    isAuthPromptDismissed,
    setAuthPromptDismissed,
    getStoredThreadId,
    setStoredThreadId,
    clearStoredThreadId,
    notifyChatReset,
    ADD_VEHICLE_TAB_ID,
    CLOSE_ADD_VEHICLE_TAB_EVENT,
    SESSION_EXPIRED_MESSAGE,
} from '../utils/chatIdentity';
import {
    DEFAULT_COUNTRY,
    getPhoneValidationError,
    normalizePhoneForOtp,
} from '../utils/phoneValidation';
import {
    getEmailAuthErrorMessage,
    getEmailValidationError,
    normalizeEmail,
} from '../utils/emailValidation';
import { PhoneLinkPrompt, PhoneAuthModal } from './PhoneAuth';
import ProfileSetupModal from './ProfileSetupModal';
import { clearPendingPromoCode, getPendingPromoCode, promoReplyText } from '../utils/promo';
import HelpSupportModal from './HelpSupportModal';
import { PwaInstallPrompt } from './PwaInstallPrompt';
// import OnboardingOverlay from './OnboardingPage'; // #711: infographic intro disabled
import { partnerLoginUrl } from '../utils/siteHost';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { buildLoginConsentPayload } from '../config/loginConsent';
import { acceptLoginConsent, isLoginConsentRequired } from '../utils/loginConsentApi';
import {
    fetchNotifications,
    markNotificationReadApi,
    markAllNotificationsReadApi,
    dismissNotificationApi,
    backfillNotifications,
} from '../utils/notifications';
const API_BASE_URL = getApiBaseUrl();
const AUTH_API_BASE_URL = `${API_BASE_URL}/auth`;
const CHAT_API_BASE_URL = `${API_BASE_URL}/api/chat/secure`;
const OTP_LENGTH = 6;
const THREAD_HISTORY_LIMIT = 200;

const normalizeOtpInput = (value) => value.replace(/\D/g, '').slice(0, OTP_LENGTH);

// Complete = everything the "Tell us about you" step collects is on the account.
function isProfileIncomplete(auth) {
    return auth?.status === 'authenticated'
        && (!auth.profileName || !auth.profileRegion || !auth.phone || !auth.email);
}

async function parseJsonIfPresent(response) {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
        return null;
    }

    try {
        return await response.json();
    } catch {
        return null;
    }
}

const GuestDataMigrationPrompt = ({ preview, busy, error, onConfirm, onSkip }) => {
    const items = [
        ['vehicle', preview?.vehicles],
        ['saved memory', preview?.memories],
        ['document', preview?.documents],
        ['diagnostic ticket', preview?.tickets],
    ].filter(([, count]) => Number(count) > 0)
        .map(([label, count]) => `${count} ${label}${count === 1 ? '' : 's'}`);

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4 py-6">
            <div className="w-full max-w-md rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-2xl sm:p-5">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F2F2F2] text-[#414141]">
                        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[16px] font-black text-[#414141]">Move guest data to your account?</p>
                        <p className="mt-1 text-[13px] font-medium leading-relaxed text-[#414141]">
                            {items.length
                                ? `We found ${items.join(', ')} from before you logged in on this browser. Move them into your account?`
                                : 'We found activity from before you logged in on this browser. Move it into your account?'}
                        </p>
                    </div>
                </div>

                {error && <p className="mt-3 text-xs font-bold text-red-600">{error}</p>}

                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className="inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60"
                    >
                        {busy ? 'Moving...' : 'Move data'}
                    </button>
                    <button
                        type="button"
                        onClick={onSkip}
                        disabled={busy}
                        className="inline-flex h-11 flex-1 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:opacity-60"
                    >
                        Keep account clean
                    </button>
                </div>
            </div>
        </div>
    );
};

function getTicketIdentityKey(ticket, fallbackIndex = 0) {
    return String(
        ticket?.diagnostic_ticket_id ||
        ticket?.ticket_number ||
        ticket?.id ||
        ticket?.source_repair_log_id ||
        fallbackIndex
    );
}

function getTicketSortTimestamp(ticket) {
    const raw = ticket?.created_at || ticket?.createdAt || ticket?.opened_at || ticket?.updated_at || ticket?.updatedAt;
    const time = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(time) ? time : null;
}

function getTicketSortNumericId(ticket) {
    const raw = ticket?.ticket_number || ticket?.diagnostic_ticket_id || ticket?.id || ticket?.source_repair_log_id;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER;
}

function withPerUserTicketNumbers(tickets = []) {
    const numbered = new Map();

    [...tickets]
        .map((ticket, index) => ({ ticket, index }))
        .sort((a, b) => {
            const aTime = getTicketSortTimestamp(a.ticket);
            const bTime = getTicketSortTimestamp(b.ticket);
            if (aTime !== null && bTime !== null && aTime !== bTime) return aTime - bTime;
            if (aTime !== null && bTime === null) return -1;
            if (aTime === null && bTime !== null) return 1;

            const aId = getTicketSortNumericId(a.ticket);
            const bId = getTicketSortNumericId(b.ticket);
            if (aId !== bId) return aId - bId;
            return a.index - b.index;
        })
        .forEach(({ ticket, index }, sequenceIndex) => {
            numbered.set(getTicketIdentityKey(ticket, index), sequenceIndex + 1);
        });

    return tickets.map((ticket, index) => ({
        ...ticket,
        display_ticket_number: numbered.get(getTicketIdentityKey(ticket, index)) || index + 1,
    }));
}

function getDiagnosticTicketTitle(ticket) {
    return ticket?.title || ticket?.symptom_summary || '';
}

function isNonDiagnosticTicketTitle(title = '') {
    const normalized = String(title)
        .trim()
        .toLowerCase()
        .replace(/[^\w\s']/g, '')
        .replace(/\s+/g, ' ');
    return [
        'new chat',
        'hi',
        'hii',
        'hiii',
        'hello',
        'hey',
        'hiya',
        'hlo',
        'yo',
        'sup',
        'good morning',
        'good afternoon',
        'good evening',
        'whats up',
        "what's up",
        'how are you',
    ].includes(normalized);
}

function isDisplayableDiagnosticTicket(ticket) {
    return Boolean(ticket) && !isNonDiagnosticTicketTitle(getDiagnosticTicketTitle(ticket));
}

function getActiveDiagnosticTicketSignature(ticket) {
    if (!ticket) return '';
    return [
        ticket?.thread_id || '',
        ticket?.diagnostic_ticket_id || '',
        ticket?.display_ticket_number || ticket?.ticket_number || '',
        ticket?.status || '',
        ticket?.title || ticket?.symptom_summary || '',
        ticket?.vehicle_label || ticket?.vehicleLabel || '',
    ].join('|');
}

function getDiagnosticVehicleLabel(ticket) {
    if (ticket?.vehicle_label) return ticket.vehicle_label;
    if (ticket?.vehicleLabel) return ticket.vehicleLabel;
    const vehicle = ticket?.vehicle || {};
    return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
}

function getDiagnosticTicketThreadId(ticket) {
    return (
        ticket?.thread_id ||
        ticket?.threadId ||
        ticket?.source_repair_log_id ||
        ticket?.sourceRepairLogId ||
        ticket?.repair_log_id ||
        ticket?.repairLogId ||
        ''
    );
}

function getDiagnosticTicketRealId(ticket) {
    return (
        ticket?.diagnostic_ticket_id ||
        ticket?.diagnosticTicketId ||
        ticket?.ticket_id ||
        ticket?.ticketId ||
        ticket?.id ||
        ''
    );
}

function isDiagnosticTicketClosed(ticket) {
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    return status === 'closed' || status === 'resolved' || status === 'abandoned';
}

function diagnosticTicketsHaveExactIdentity(a, b) {
    if (!a || !b) return false;

    const aRealId = getDiagnosticTicketRealId(a);
    const bRealId = getDiagnosticTicketRealId(b);
    if (aRealId && bRealId && String(aRealId) === String(bRealId)) return true;

    const aThreadId = getDiagnosticTicketThreadId(a);
    const bThreadId = getDiagnosticTicketThreadId(b);
    return Boolean(aThreadId && bThreadId && String(aThreadId) === String(bThreadId));
}

function diagnosticTicketHasIdentity(ticket) {
    return Boolean(getDiagnosticTicketRealId(ticket) || getDiagnosticTicketThreadId(ticket));
}

function diagnosticTicketsReferToSameIssue(a, b) {
    if (!a || !b) return false;

    if (diagnosticTicketsHaveExactIdentity(a, b)) return true;
    if (diagnosticTicketHasIdentity(a) && diagnosticTicketHasIdentity(b)) return false;

    const aIssueKey = normalizeDiagnosticIssueKey(getDiagnosticTicketTitle(a));
    const bIssueKey = normalizeDiagnosticIssueKey(getDiagnosticTicketTitle(b));
    if (!aIssueKey || !bIssueKey || aIssueKey !== bIssueKey) return false;

    const aVehicleKey = getDiagnosticVehicleLabel(a).trim().toLowerCase();
    const bVehicleKey = getDiagnosticVehicleLabel(b).trim().toLowerCase();
    return !aVehicleKey || !bVehicleKey || aVehicleKey === bVehicleKey;
}

function mergeDiagnosticTicketForUi(existingTicket, nextTicket) {
    return {
        ...existingTicket,
        ...nextTicket,
        display_ticket_number: existingTicket?.display_ticket_number || nextTicket?.display_ticket_number,
        ticket_number: existingTicket?.ticket_number || nextTicket?.ticket_number,
        thread_id: nextTicket?.thread_id || existingTicket?.thread_id,
        source_repair_log_id: nextTicket?.source_repair_log_id || existingTicket?.source_repair_log_id,
        vehicle: existingTicket?.vehicle || nextTicket?.vehicle,
    };
}

function applyClosedDiagnosticOverrides(tickets = [], previousTickets = []) {
    const closedOverrides = (previousTickets || []).filter(isDiagnosticTicketClosed);
    if (!closedOverrides.length) return tickets;

    const mergedTickets = (tickets || []).map((ticket) => {
        const override = closedOverrides.find((closedTicket) => diagnosticTicketsHaveExactIdentity(ticket, closedTicket));
        return override ? mergeDiagnosticTicketForUi(ticket, override) : ticket;
    });

    const additionalClosedTickets = closedOverrides.filter((closedTicket) => (
        !mergedTickets.some((ticket) => diagnosticTicketsHaveExactIdentity(ticket, closedTicket))
    ));

    return [...additionalClosedTickets, ...mergedTickets];
}

function normalizeDiagnosticIssueKey(value = '') {
    const rawTitle = String(value || '').toLowerCase();
    const normalized = rawTitle
        .replace(/won['â€™]?t/g, 'will not')
        .replace(/\bcan['â€™]?t\b/g, 'cannot')
        .replace(/\bdoesn['â€™]?t\b/g, 'does not')
        .replace(/\bisn['â€™]?t\b/g, 'is not')
        .replace(/\bno[\s-]*start(?:ing)?\b/g, 'no start')
        .replace(/\bnot starting\b/g, 'no start')
        .replace(/\bwill not start\b/g, 'no start')
        .replace(/\bwon?t start\b/g, 'no start')
        .replace(/\bopening\b/g, 'open')
        .replace(/\bopens\b/g, 'open')
        .replace(/\bopened\b/g, 'open')
        .replace(/\brotating\b/g, 'rotate')
        .replace(/\brotates\b/g, 'rotate')
        .replace(/\bcar\b/g, ' ')
        .replace(/\bvehicle\b/g, ' ')
        .replace(/\bmy\b/g, ' ')
        .replace(/\bis\b/g, ' ')
        .replace(/\bthe\b/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\bstarting\b/g, 'start')
        .replace(/\bstarts\b/g, 'start')
        .replace(/\s+/g, ' ')
        .trim();

    if (
        normalized.includes('start') &&
        (normalized.includes('no start') || normalized.includes('not start') || normalized.includes('cannot start'))
    ) {
        return 'no-start';
    }

    return normalized;
}

function attachDiagnosticThreadIds(tickets = [], threads = []) {
    return tickets.map((ticket) => {
        const directThread = threads.find((thread) => (
            String(thread?.thread_id) === String(ticket?.thread_id || '') ||
            String(thread?.thread_id) === String(ticket?.source_repair_log_id || '') ||
            String(thread?.thread_id) === String(ticket?.repair_log_id || '')
        ));

        if (directThread) {
            return { ...ticket, thread_id: directThread.thread_id };
        }

        return ticket;
    });
}

// True when a diagnostic ticket belongs to the given conversation thread — matched
// across the several id fields a ticket can carry its source thread under.
function diagnosticTicketMatchesThread(ticket, threadId) {
    if (!ticket || threadId == null) return false;
    const target = String(threadId);
    return [ticket.thread_id, ticket.source_repair_log_id, ticket.repair_log_id, ticket.source_session_id]
        .some((id) => id != null && String(id) === target);
}

const Layout = () => {
    const navigate = useNavigate();
    const [authState, setAuthState] = useState({
        status: 'loading',
        phone: '',
        // An account can be identified by phone, email, or both.
        email: '',
        // The key user data is stored under: phone when there is one, else email.
        // Mirrors get_identity() in auth/session.py. Use this — never `phone` alone —
        // whenever an account needs identifying, or email-only accounts silently miss.
        // `phone` stays for display and for genuine phone-number fields only.
        identity: '',
        userId: null,
        // users.display_name / users.region, from /auth/me. Drive the profile step.
        profileName: '',
        profileRegion: '',
        promptDismissed: isAuthPromptDismissed(),
    });
    const [adminAuthState, setAdminAuthState] = useState({
        status: 'loading',
        authenticated: false,
    });
    const location = useLocation();
    const isChatPage = location.pathname === '/chat' || location.pathname === '/test-chat';
    const isShopRoute = location.pathname === '/shop' || location.pathname.startsWith('/shop/');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [pendingQuestion, setPendingQuestion] = useState(null);
    // const [showOnboarding, setShowOnboarding] = useState(false); // #711

    // Thread state
    const [threads, setThreads] = useState([]);
    const [currentThreadId, setCurrentThreadId] = useState(null);
    const requestedThreadId = location.state?.selectThreadId || null;
    const requestedThreadRef = useRef(requestedThreadId);
    requestedThreadRef.current = requestedThreadId;
    const selectedThreadMetadataRef = useRef(null);
    const [threadSelection, setThreadSelection] = useState(null);
    const selectionReady = !requestedThreadId || (
        authState.status === 'authenticated'
        && threadSelection?.status === 'ready'
        && String(threadSelection.threadId) === String(requestedThreadId)
        && threadSelection.identity === authState.identity
        && String(currentThreadId) === String(requestedThreadId)
    );
    // Each "Add Vehicle" is its OWN real conversation with the card pinned to the top; multiple can
    // coexist and none vanish. This holds the set of thread ids that are add-vehicle chats (so the
    // card renders at the top of each). Stored in localStorage so it persists across reloads, new
    // tabs, `npm start`, and browser restarts (sessionStorage would be empty in a fresh tab).
    // Whether the Add-Vehicle card is on screen is UI state, so it must never depend on owning
    // an authenticated server resource. When there is no thread behind the card - a signed-out
    // visitor, or a signed-in one whose thread creation failed - it lives on this client-side id
    // instead. Without it the card could only ever open for someone with a valid session, which
    // is why the button did nothing at all for anonymous users.
    const [localAddVehicleCardId, setLocalAddVehicleCardId] = useState(null);
    const [addVehicleThreadIds, setAddVehicleThreadIds] = useState(() => {
        try { return JSON.parse(localStorage.getItem('dash_add_vehicle_threads') || '[]'); } catch { return []; }
    });
    useEffect(() => {
        try { localStorage.setItem('dash_add_vehicle_threads', JSON.stringify(addVehicleThreadIds)); } catch { /* ignore */ }
    }, [addVehicleThreadIds]);
    const [threadsLoading, setThreadsLoading] = useState(false);
    // The identity whose thread list has finished loading. Lets work that opens a chat
    // wait for that load, so the load cannot switch the chat away afterwards.
    const [threadsLoadedFor, setThreadsLoadedFor] = useState('');
    const [recentDiagnostics, setRecentDiagnostics] = useState([]);
    const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
    // Partner-estimate (shop) conversations for the sidebar — each sent estimate shows as a
    // row titled with the shop's name.
    const [garageRequests, setGarageRequests] = useState([]);
    const [activeDiagnosticTicket, setActiveDiagnosticTicket] = useState(null);
    const diagnosticsRequestSeqRef = useRef(0);
    // Conversations archived during this session. A diagnosis conversation surfaces from
    // TWO sources (the thread list AND the diagnostic-tickets endpoint); the thread list
    // already drops archived ones server-side, but the tickets feed can lag a refresh, so
    // we keep a local guard to hide them immediately and keep them hidden until restore.
    const [locallyArchivedThreadIds, setLocallyArchivedThreadIds] = useState(() => new Set());
    // Driver notifications (real feed from user_details /api/notifications).
    const [notifications, setNotifications] = useState([]);
    const [notificationsUnread, setNotificationsUnread] = useState(0);
    // Tracks the identity we've already run history-backfill for (once per session).
    const notificationsBackfilledRef = useRef(null);
    // Connectivity banner ("No connection — reconnecting…" / "Back online"). Browser online
    // state is the immediate signal; server reachability is tracked off the notifications
    // poll so a server-down-while-Wi-Fi-up case is also caught.
    const [isBrowserOnline, setIsBrowserOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
    const [isServerReachable, setIsServerReachable] = useState(true);

    // Display name from user profile
    const [displayName, setDisplayName] = useState('');

    const refreshDisplayName = useCallback(async () => {
        if (authState.status !== 'authenticated' || !authState.identity) {
            setDisplayName('');
            return '';
        }

        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/lookup_user_data?phone_number=${encodeURIComponent(authState.identity)}`);
            if (!response.ok) return '';

            const data = await response.json();
            const nextName = data?.result?.username || '';
            setDisplayName(nextName);
            return nextName;
        } catch {
            return '';
        }
    }, [authState.status, authState.identity]);

    useEffect(() => {
        refreshDisplayName();
    }, [refreshDisplayName]);

    // Driver profile picture (presigned URL) — the single source for the sidebar and
    // Settings. Seeded from /auth/me; Settings updates it after a save/remove.
    const [avatarUrl, setAvatarUrl] = useState('');

    // Auth Flow State
    const [authMode, setAuthMode] = useState('phone'); // 'phone' or 'admin'
    const [authChannel, setAuthChannel] = useState('phone'); // 'phone' or 'email'
    const [authStep, setAuthStep] = useState('phone'); // 'phone' or 'otp'
    // phoneInput stores the full value from react-phone-input-2 (dialCode + nationalDigits, no '+')
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
    const [emailInput, setEmailInput] = useState('');
    const [otpInput, setOtpInput] = useState('');
    // #739: consent is collected once per copy version. After the code is verified the
    // backend says whether this user still has to accept the current version; only then does
    // the modal show the consent step (authStep 'consent') before the session is created.
    const [serviceConsentAccepted, setServiceConsentAccepted] = useState(false);
    const [marketingConsentOptIn, setMarketingConsentOptIn] = useState(false);
    // { token, reason, verifyData } from a "consent_required" verify response.
    const [loginConsentRequest, setLoginConsentRequest] = useState(null);
    // Page the login was started from, stored with the consent record.
    const loginSourcePageRef = useRef('');

    useEffect(() => {
        if (!isAuthModalOpen) return;
        setServiceConsentAccepted(false);
        setMarketingConsentOptIn(false);
        setLoginConsentRequest(null);
        loginSourcePageRef.current = `${location.pathname}${location.search || ''}`;
        // Only on open: navigating while the modal is open must not clear a ticked box.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthModalOpen]);

    const getLoginConsentPayload = () => buildLoginConsentPayload({
        serviceAccepted: serviceConsentAccepted,
        marketingOptIn: marketingConsentOptIn,
        sourcePage: loginSourcePageRef.current,
    });
    const [pendingPhone, setPendingPhone] = useState('');
    const [pendingEmail, setPendingEmail] = useState('');
    const [authError, setAuthError] = useState(null);
    const [authNotice, setAuthNotice] = useState(null);
    const [loginNotice, setLoginNotice] = useState(null);
    const sessionExpiredRef = useRef(false);
    const [authBusy, setAuthBusy] = useState(false);
    const [guestMigrationPreview, setGuestMigrationPreview] = useState(null);
    const [guestMigrationBusy, setGuestMigrationBusy] = useState(false);
    const [guestMigrationError, setGuestMigrationError] = useState(null);
    const {
        secondsRemaining: otpResendSecondsRemaining,
        isCoolingDown: isOtpResendCoolingDown,
        start: startOtpResendCooldown,
        reset: resetOtpResendCooldown,
    } = useOtpResendCooldown();

    const refreshAuthState = useCallback(async () => {
        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/me`, {
                credentials: 'include',
            });

            const data = await parseJsonIfPresent(response);

            if (!response.ok || !data?.authenticated) {
                setAuthState((prev) => ({
                    ...prev,
                    status: 'anonymous',
                    phone: '',
                    email: '',
                    identity: '',
                    userId: null,
                    profileName: '',
                    profileRegion: '',
                }));
                setAvatarUrl('');
                return;
            }

            setAuthState((prev) => ({
                ...prev,
                status: 'authenticated',
                phone: data.phone || '',
                email: data.email || '',
                identity: data.phone || data.email || '',
                userId: data.user_id ?? null,
                profileName: data.display_name || '',
                profileRegion: data.region || '',
            }));
            setAvatarUrl(data.avatar_url || '');
        } catch (error) {
            console.error('Failed to restore auth session:', error);
            setAvatarUrl('');
            setAuthState((prev) => ({
                ...prev,
                status: 'anonymous',
                phone: '',
                email: '',
                identity: '',
                userId: null,
                profileName: '',
                profileRegion: '',
            }));
        }
    }, []);

    useEffect(() => {
        refreshAuthState();
    }, [refreshAuthState]);

    const refreshAdminAuthState = useCallback(async () => {
        try {
            const shopResponse = await fetch(`${getApiBaseUrl()}/shop-auth/me`, {
                credentials: 'include',
            });
            const shopData = await parseJsonIfPresent(shopResponse);
            if (shopResponse.ok && shopData?.authenticated) {
                setAdminAuthState({ status: 'authenticated', authenticated: true });
                return true;
            }

            const adminResponse = await fetch(`${AUTH_API_BASE_URL}/admin/me`, {
                credentials: 'include',
            });
            const adminData = await parseJsonIfPresent(adminResponse);
            if (adminResponse.ok && adminData?.authenticated) {
                setAdminAuthState({ status: 'authenticated', authenticated: true });
                return true;
            }

            setAdminAuthState({ status: 'anonymous', authenticated: false });
            return false;
        } catch (error) {
            console.error('Failed to restore shop session:', error);
            setAdminAuthState({ status: 'anonymous', authenticated: false });
            return false;
        }
    }, []);

    useEffect(() => {
        refreshAdminAuthState();
    }, [refreshAdminAuthState]);

    const promptLogin = useCallback((notice) => {
        setLoginNotice(notice || null);
        setIsAuthModalOpen(true);
        setAuthMode('phone');
        setAuthChannel('phone');
        setAuthStep('phone');
        setAuthError(null);
        setAuthNotice(null);
        resetOtpResendCooldown();
    }, [resetOtpResendCooldown]);

    const handleSessionExpired = useCallback(() => {
        if (sessionExpiredRef.current) return;
        sessionExpiredRef.current = true;
        setAuthState((prev) => ({
            ...prev,
            status: 'anonymous',
            phone: '',
            email: '',
            identity: '',
            userId: null,
            profileName: '',
            profileRegion: '',
        }));
        setThreads([]);
        setCurrentThreadId(null);
        promptLogin(SESSION_EXPIRED_MESSAGE);
    }, [promptLogin]);

    // Re-arm the once-per-loss guard as soon as a session is valid again.
    useEffect(() => {
        if (authState.status === 'authenticated') sessionExpiredRef.current = false;
    }, [authState.status]);

    //Thread management

    const fetchThreads = useCallback(async (phone) => {
        if (!phone) return;
        setThreadsLoading(true);
        try {
            const res = await fetch(`${CHAT_API_BASE_URL}/threads?limit=${THREAD_HISTORY_LIMIT}`, { credentials: 'include', cache: 'no-store' });
            if (res.status === 401) { handleSessionExpired(); return; }
            if (!res.ok) return;
            const data = await res.json();
            const list = data.threads || [];
            const requestedId = requestedThreadRef.current;
            const selectedMetadata = selectedThreadMetadataRef.current;
            setThreads(requestedId && String(selectedMetadata?.thread_id) === String(requestedId)
                && !list.some((thread) => String(thread.thread_id) === String(requestedId))
                ? [selectedMetadata, ...list] : list);
            const storedThreadId = getStoredThreadId(phone);
            const hasStoredThread = storedThreadId != null
                && list.some((thread) => String(thread.thread_id) === String(storedThreadId));
            setCurrentThreadId(requestedId || (hasStoredThread ? storedThreadId : null));
        } catch (e) {
            console.error('[Threads] fetchThreads failed', e);
        } finally {
            setThreadsLoading(false);
            // Marked even when the load failed: anything waiting on it must still run.
            setThreadsLoadedFor(phone);
        }
    }, [handleSessionExpired]);

    const createThread = useCallback(async (initialTitle = null, options = {}) => {
        if (requestedThreadRef.current && !selectionReady) return null;
        const cleanedTitle = typeof initialTitle === 'string' ? initialTitle.trim() : '';
        const shouldActivate = options?.activate !== false;
        try {
            const res = await fetch(`${CHAT_API_BASE_URL}/threads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(cleanedTitle ? { title: cleanedTitle } : {}),
            });
            if (res.status === 401) { handleSessionExpired(); return null; }
            if (!res.ok) throw new Error('Failed to create thread');
            const thread = await res.json();
            const serverTitle = String(thread.title || '').trim();
            const threadToAdd = cleanedTitle
                ? { ...thread, title: serverTitle && serverTitle !== 'New Chat' ? serverTitle : cleanedTitle }
                : thread;
            setThreads(prev => [threadToAdd, ...prev]);
            if (shouldActivate) {
                setCurrentThreadId(thread.thread_id);
                if (authState.identity) setStoredThreadId(authState.identity, thread.thread_id);
            }
            return thread;
        } catch (e) {
            console.error('[Threads] createThread failed', e);
            return null;
        }
    }, [authState.identity, handleSessionExpired, selectionReady]);

    const startNewChat = useCallback(() => {
        setCurrentThreadId(null);
        setActiveDiagnosticTicket(null);
        setLocalAddVehicleCardId(null);
        if (authState.identity) clearStoredThreadId(authState.identity);
        notifyChatReset('fresh-chat');
        navigate('/chat', { replace: true, state: null });
    }, [authState.identity, navigate]);

    const selectThread = useCallback((threadId) => {
        if (requestedThreadId && String(requestedThreadId) !== String(threadId)) {
            navigate('/chat', { replace: true, state: null });
        }
        setCurrentThreadId(threadId);
        if (authState.identity) setStoredThreadId(authState.identity, threadId);

        // Switch the active vehicle to match the selected chat's vehicle, so the
        // vehicle selector, placeholder, and CarFido's context all follow the chat.
        if (!authState.identity) return;
        const thread = threads.find((t) => String(t.thread_id) === String(threadId));
        const diag = (recentDiagnostics || []).find((t) => (
            String(t.thread_id || t.source_repair_log_id || t.repair_log_id || '') === String(threadId)
        ));
        const vehicleId = thread?.vehicle_id || diag?.vehicle_id || diag?.vehicle?.vehicle_id || null;
        if (!vehicleId) return;
        fetch(`${getUserDetailsBaseUrl()}/api/vehicle_selection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phone_number: authState.identity, vehicle_id: vehicleId }),
        })
            .then(() => window.dispatchEvent(new CustomEvent('dash_vehicle_switched')))
            .catch((err) => console.warn('Vehicle switch on thread select failed:', err));
    }, [authState.identity, threads, recentDiagnostics, navigate, requestedThreadId]);

    useEffect(() => {
        const threadId = requestedThreadId;
        if (!threadId || authState.status !== 'authenticated') return;
        let cancelled = false;
        selectedThreadMetadataRef.current = null;
        setThreadSelection({ threadId, identity: authState.identity, status: 'loading' });
        const resolve = async () => {
            try {
                if (!/^[1-9]\d*$/.test(String(threadId))) throw new Error('Chat unavailable. Please contact the shop.');
                const response = await fetch(`${CHAT_API_BASE_URL}/threads/${threadId}`, { credentials: 'include', cache: 'no-store' });
                if (cancelled) return;
                if (response.status === 401) {
                    handleSessionExpired();
                    return;
                }
                const data = await response.json().catch(() => ({}));
                if (!response.ok || String(data?.thread_id) !== String(threadId)) {
                    throw new Error(typeof data?.detail === 'string' ? data.detail : 'Referral chat unavailable. Please contact the shop.');
                }
                if (cancelled) return;
                selectedThreadMetadataRef.current = data;
                setThreads((prev) => [
                    { ...prev.find((thread) => String(thread.thread_id) === String(threadId)), ...data },
                    ...prev.filter((thread) => String(thread.thread_id) !== String(threadId)),
                ]);
                setCurrentThreadId(threadId);
                setStoredThreadId(authState.identity, threadId);
                setThreadSelection({ threadId, identity: authState.identity, status: 'ready' });
            } catch (error) {
                if (!cancelled) setThreadSelection({ threadId, identity: authState.identity, status: 'error', error: error.message || 'Could not open this chat. Please try the referral link again.' });
            }
        };
        resolve();
        return () => { cancelled = true; };
    }, [requestedThreadId, authState.status, authState.identity, handleSessionExpired]);


    useEffect(() => {
        const handleOpenAddVehicle = async (event) => {
            // Carry any parsed year/make/model/odometer through so the form opens pre-filled.
            const prefill = event?.detail?.prefill || null;
            const userMessage = event?.detail?.userMessage || '';
            setActiveDiagnosticTicket(null);
            const openCard = (cardId) => navigate('/chat', {
                state: {
                    openVehicleForm: true,
                    vehicleFormPrefill: prefill,
                    addVehicleThreadId: cardId,
                    addVehicleUserMessage: userMessage,
                },
            });

            // Signed in and already inside a chat: keep the setup card inline in this
            // conversation. This is especially important for referral-locked intake
            // threads, where creating a separate "Add Vehicle" chat pulls the driver
            // out of the shop handoff.
            if (authState.status === 'authenticated' && currentThreadId) {
                const id = String(currentThreadId);
                setAddVehicleThreadIds((prev) => (prev.includes(id) ? prev : [id, ...prev]));
                setLocalAddVehicleCardId(null);
                openCard(id);
                return;
            }

            // Signed in with no selected chat: create a durable place for the setup card.
            if (authState.status === 'authenticated') {
                const thread = await createThread('Add Vehicle', { activate: true });
                if (thread?.thread_id) {
                    const id = String(thread.thread_id);
                    setAddVehicleThreadIds((prev) => (prev.includes(id) ? prev : [id, ...prev]));
                    setLocalAddVehicleCardId(null);
                    openCard(id);
                    return;
                }
                // Thread creation failed (offline, rate limited, session died mid-flight).
                // Fall through rather than dead-ending: the card below still opens and the
                // vehicle still saves — only the "this add-vehicle keeps its own chat" is lost.
            }

            // Anonymous visitors, and the failure case above. The card is client state, so open
            // it on a local id and touch no secure endpoint — there is nothing here that can 401.
            // Saving works either way: the form posts the web_ id for a guest, which
            // update_user_car already accepts, and it carries over when they sign up.
            setLocalAddVehicleCardId(`local-add-vehicle-${Date.now().toString(36)}`);
            openCard(null);
        };
        const handleCloseAddVehicleTab = (event) => {
            // Unmark a specific add-vehicle thread (its card is done) — the conversation stays as a
            // normal chat; other add-vehicle chats are untouched.
            const id = event?.detail?.threadId != null ? String(event.detail.threadId) : null;
            setAddVehicleThreadIds((prev) => (id ? prev.filter((t) => t !== id) : prev));
            // A card with no thread behind it reports a null threadId — clear the local id or
            // the ✕ (and a successful save) would leave the card stuck on screen.
            if (id == null) setLocalAddVehicleCardId(null);
        };
        window.addEventListener('dash_open_add_vehicle', handleOpenAddVehicle);
        window.addEventListener(CLOSE_ADD_VEHICLE_TAB_EVENT, handleCloseAddVehicleTab);
        return () => {
            window.removeEventListener('dash_open_add_vehicle', handleOpenAddVehicle);
            window.removeEventListener(CLOSE_ADD_VEHICLE_TAB_EVENT, handleCloseAddVehicleTab);
        };
    }, [navigate, createThread, authState.status, currentThreadId]);

    const deleteThread = useCallback(async (threadId) => {
        try {
            const res = await fetch(`${CHAT_API_BASE_URL}/threads/${threadId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ is_deleted: true }),
            });
            // Don't optimistically drop the thread from the sidebar when the server refused -
            // the delete didn't happen, and it would silently reappear on the next poll.
            if (res.status === 401) { handleSessionExpired(); return; }
            if (String(requestedThreadRef.current) === String(threadId)) {
                navigate('/chat', { replace: true, state: null });
            }
            setThreads(prev => {
                const remaining = prev.filter(t => t.thread_id !== threadId);
                // If the deleted thread was active, switch to the next one
                setCurrentThreadId(cur => {
                    if (cur !== threadId) return cur;
                    const next = remaining[0] || null;
                    if (next && authState.identity) setStoredThreadId(authState.identity, next.thread_id);
                    return next ? next.thread_id : null;
                });
                return remaining;
            });
        } catch (e) {
            console.error('[Threads] deleteThread failed', e);
        }
    }, [authState.identity, handleSessionExpired, navigate]);

    // Rename a conversation. A chat thread renames via the thread PATCH (which also locks
    // the title so the auto-namer never overwrites it). A diagnosis conversation ALSO shows
    // its ticket title in the sidebar, so when a ticketId is supplied we rename the ticket
    // too — otherwise the diagnosis card would snap back to the old name on the next refresh.
    const renameThread = useCallback(async (threadId, title, ticketId = null) => {
        const nextTitle = (title || '').trim();
        if (!nextTitle) return;
        try {
            if (threadId != null) {
                const res = await fetch(`${CHAT_API_BASE_URL}/threads/${threadId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ title: nextTitle }),
                });
                if (res.status === 401) { handleSessionExpired(); return; }
                setThreads(prev => prev.map(t => t.thread_id === threadId ? { ...t, title: nextTitle } : t));
            }
            if (ticketId != null && authState.identity) {
                await fetch(`${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(authState.identity)}/${ticketId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ title: nextTitle }),
                });
            }
            // Update the diagnosis-card feed too, matched by thread or ticket id.
            setRecentDiagnostics(prev => prev.map(t => (
                (ticketId != null && String(t.diagnostic_ticket_id) === String(ticketId)) ||
                (threadId != null && diagnosticTicketMatchesThread(t, threadId))
                    ? { ...t, title: nextTitle }
                    : t
            )));
        } catch (e) {
            console.error('[Threads] renameThread failed', e);
        }
    }, [handleSessionExpired, authState.identity]);

    const refreshDiagnosticTickets = useCallback(async () => {
        if (authState.status !== 'authenticated' || !authState.identity) {
            setRecentDiagnostics([]);
            return [];
        }

        const requestSeq = diagnosticsRequestSeqRef.current + 1;
        diagnosticsRequestSeqRef.current = requestSeq;
        setDiagnosticsLoading(true);
        try {
            const res = await fetch(
                `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(authState.identity)}?include_closed=true`
            );
            if (!res.ok) return [];
            const data = await res.json();
            if (requestSeq !== diagnosticsRequestSeqRef.current) return [];
            const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
            const numberedTickets = withPerUserTicketNumbers(tickets.filter(isDisplayableDiagnosticTicket));
            setRecentDiagnostics((prev) => (
                withPerUserTicketNumbers(applyClosedDiagnosticOverrides(numberedTickets, prev).filter(isDisplayableDiagnosticTicket))
            ));
            return numberedTickets;
        } catch (e) {
            console.error('[Tickets] fetch failed', e);
            return [];
        } finally {
            if (requestSeq === diagnosticsRequestSeqRef.current) {
                setDiagnosticsLoading(false);
            }
        }
    }, [authState.status, authState.identity]);

    // All of this driver's partner-estimate (shop) requests, for the sidebar. No params =
    // the session user's own requests. Best-effort: a failure just leaves the list empty.
    const refreshGarageRequests = useCallback(async () => {
        if (authState.status !== 'authenticated' || !authState.identity) {
            setGarageRequests([]);
            return;
        }
        try {
            const requests = await listPartnerRepairRequests();
            setGarageRequests(Array.isArray(requests) ? requests : []);
        } catch (_) {
            /* leave the previous list in place on a transient failure */
        }
    }, [authState.status, authState.identity]);

    // Archive a conversation: it leaves the active list and stops contributing to AI/vehicle
    // context (server-side), but is kept and restorable with full history. Never deletes.
    const archiveThread = useCallback(async (threadId) => {
        try {
            const url = `${CHAT_API_BASE_URL}/threads/${threadId}`;
            console.log('[Archive] PATCH', url, '{is_archived:true}');
            const res = await fetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ is_archived: true }),
            });
            if (res.status === 401) { handleSessionExpired(); return; }
            if (!res.ok) {
                let detail = '';
                try { detail = await res.text(); } catch (_) { /* ignore */ }
                console.error('[Archive] FAILED', res.status, detail);
                alert(`Archive failed (HTTP ${res.status}). ${detail || ''}`.trim());
                return;
            }
            console.log('[Archive] OK', threadId);
            if (String(requestedThreadRef.current) === String(threadId)) {
                navigate('/chat', { replace: true, state: null });
            }
            setThreads(prev => {
                const remaining = prev.filter(t => t.thread_id !== threadId);
                setCurrentThreadId(cur => {
                    if (cur !== threadId) return cur;
                    const next = remaining[0] || null;
                    if (next && authState.identity) setStoredThreadId(authState.identity, next.thread_id);
                    return next ? next.thread_id : null;
                });
                return remaining;
            });
            // A diagnosis conversation also surfaces as a diagnostic ticket (a separate
            // data source), so drop any ticket tied to this thread too — otherwise the
            // archived conversation would linger in the sidebar as its diagnosis card.
            setRecentDiagnostics(prev => prev.filter(t => !diagnosticTicketMatchesThread(t, threadId)));
            setLocallyArchivedThreadIds(prev => {
                const next = new Set(prev);
                next.add(String(threadId));
                return next;
            });
        } catch (e) {
            console.error('[Threads] archiveThread failed', e);
            alert(`Archive failed: ${e?.message || e}`);
        }
    }, [authState.identity, handleSessionExpired, navigate]);

    // Restore an archived conversation back to the active list with its history intact.
    const restoreThread = useCallback(async (threadId) => {
        try {
            const res = await fetch(`${CHAT_API_BASE_URL}/threads/${threadId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ is_archived: false }),
            });
            if (res.status === 401) { handleSessionExpired(); return; }
            if (!res.ok) return false;
            // Lift the local archive guard, then bring the restored conversation (and any
            // diagnosis card it carried) back into the active sidebar lists.
            setLocallyArchivedThreadIds(prev => {
                if (!prev.has(String(threadId))) return prev;
                const next = new Set(prev);
                next.delete(String(threadId));
                return next;
            });
            if (authState.identity) fetchThreads(authState.identity);
            refreshDiagnosticTickets();
            return true;
        } catch (e) {
            console.error('[Threads] restoreThread failed', e);
            return false;
        }
    }, [handleSessionExpired, authState.identity, fetchThreads, refreshDiagnosticTickets]);

    // Fetch the archived conversations (for the Archived view). Returns the list.
    const fetchArchivedThreads = useCallback(async () => {
        if (authState.status !== 'authenticated') return [];
        try {
            const res = await fetch(`${CHAT_API_BASE_URL}/threads?limit=${THREAD_HISTORY_LIMIT}&archived=true`, { credentials: 'include', cache: 'no-store' });
            if (res.status === 401) { handleSessionExpired(); return []; }
            if (!res.ok) return [];
            const data = await res.json();
            return data.threads || [];
        } catch (_) { return []; }
    }, [authState.status, handleSessionExpired]);

    // ── Notifications ────────────────────────────────────────────────────────
    const refreshNotifications = useCallback(async () => {
        if (authState.status !== 'authenticated' || !authState.identity) {
            setNotifications([]);
            setNotificationsUnread(0);
            return;
        }
        const { notifications: rows, unreadCount, ok } = await fetchNotifications(authState.identity);
        // Only overwrite the feed when the server actually answered, so a failed poll doesn't
        // blank an existing list. Connectivity is tracked separately by the internet probe
        // (the API may be on localhost, which stays reachable even with no internet).
        if (ok) {
            setNotifications(rows);
            setNotificationsUnread(unreadCount);
        }
    }, [authState.status, authState.identity]);

    const markNotificationRead = useCallback(async (notificationId) => {
        // Optimistic: flip the row + drop the unread count immediately.
        setNotifications((prev) => prev.map((n) => (
            String(n.id) === String(notificationId) && !n.isRead
                ? { ...n, isRead: true, is_read: true }
                : n
        )));
        setNotificationsUnread((prev) => Math.max(0, prev - 1));
        if (authState.identity) {
            await markNotificationReadApi(authState.identity, notificationId);
            refreshNotifications();
        }
    }, [authState.identity, refreshNotifications]);

    const dismissNotification = useCallback(async (notificationId) => {
        // Optimistic: drop the row and, if it was unread, decrement the badge.
        setNotifications((prev) => {
            const target = prev.find((n) => String(n.id) === String(notificationId));
            if (target && !target.isRead) setNotificationsUnread((u) => Math.max(0, u - 1));
            return prev.filter((n) => String(n.id) !== String(notificationId));
        });
        if (authState.identity) {
            await dismissNotificationApi(authState.identity, notificationId);
            refreshNotifications();
        }
    }, [authState.identity, refreshNotifications]);

    const markAllNotificationsRead = useCallback(async () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, is_read: true })));
        setNotificationsUnread(0);
        if (authState.identity) {
            await markAllNotificationsReadApi(authState.identity);
            refreshNotifications();
        }
    }, [authState.identity, refreshNotifications]);

    // Browser network state drives the connectivity banner immediately (the server-reach
    // check from the notifications poll is slower). Re-check the server on reconnect.
    useEffect(() => {
        const goOnline = () => { setIsBrowserOnline(true); refreshNotifications(); };
        const goOffline = () => setIsBrowserOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, [refreshNotifications]);

    // Internet-reachability heartbeat for the connectivity banner. navigator.onLine stays TRUE
    // when the router is reachable but the WAN is down, and pinging our own API can't detect
    // "no internet" in local dev (the API is on localhost and stays reachable over loopback).
    // So probe an EXTERNAL connectivity endpoint (a 204 responder) — it fails only when the
    // device truly has no internet. Short interval, short timeout, skipped while the tab is
    // hidden; a chat/stream failure triggers an immediate re-probe.
    useEffect(() => {
        let cancelled = false;
        // Tiny 204 responders used for connectivity checks (same technique browsers use). Try a
        // couple so one CDN being blocked/slow doesn't false-flag "no connection".
        const PROBE_URLS = [
            'https://www.gstatic.com/generate_204',
            'https://www.cloudflare.com/cdn-cgi/trace',
        ];
        const probeOnce = async (signal) => {
            for (const url of PROBE_URLS) {
                try {
                    await fetch(url, { mode: 'no-cors', cache: 'no-store', signal });
                    return true; // An opaque OK means the internet is reachable.
                } catch (_) {
                    // try the next endpoint
                }
            }
            return false;
        };
        const ping = async () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                if (!cancelled) setIsServerReachable(false);
                return;
            }
            const controller = new AbortController();
            const timer = window.setTimeout(() => controller.abort(), 6000);
            try {
                const reachable = await probeOnce(controller.signal);
                if (!cancelled) setIsServerReachable(reachable);
            } finally {
                window.clearTimeout(timer);
            }
        };
        ping();
        const interval = window.setInterval(ping, 5000);
        const onVisible = () => { if (!document.hidden) ping(); };
        // A chat send / stream failure asks us to re-check reachability NOW, so the banner
        // appears the instant the user hits a server they can't reach (the ping confirms it,
        // so an ordinary server-side error — where the server IS reachable — won't false-flag).
        const onCheck = () => ping();
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('dash_check_connectivity', onCheck);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('dash_check_connectivity', onCheck);
        };
    }, []);

    // Load on auth, refresh on window focus, and poll periodically so the bell
    // badge stays current without a page reload.
    useEffect(() => {
        if (authState.status !== 'authenticated') {
            setNotifications([]);
            setNotificationsUnread(0);
            return undefined;
        }
        // Once per identity, backfill history (idempotent) before the first fetch so
        // existing users see past events; subsequent loads just fetch.
        if (authState.identity && notificationsBackfilledRef.current !== authState.identity) {
            notificationsBackfilledRef.current = authState.identity;
            backfillNotifications(authState.identity).then(() => refreshNotifications());
        } else {
            refreshNotifications();
        }
        const onFocus = () => refreshNotifications();
        window.addEventListener('focus', onFocus);
        const interval = setInterval(refreshNotifications, 60000);
        return () => {
            window.removeEventListener('focus', onFocus);
            clearInterval(interval);
        };
    }, [authState.status, authState.identity, refreshNotifications]);

    // Re-fetch when the user opens the Notifications page.
    useEffect(() => {
        if (location.pathname === '/notifications') refreshNotifications();
    }, [location.pathname, refreshNotifications]);

    const refreshThreadTitles = useCallback(async () => {
        if (authState.status !== 'authenticated') return;
        try {
            const res = await fetch(`${CHAT_API_BASE_URL}/threads?limit=${THREAD_HISTORY_LIMIT}`, { credentials: 'include', cache: 'no-store' });
            if (res.status === 401) { handleSessionExpired(); return; }
            if (!res.ok) return;
            const data = await res.json();
            const updated = data.threads || [];
            setThreads(prev => {
                const updatedMap = new Map(updated.map(t => [t.thread_id, t]));
                const prevIds = new Set(prev.map(t => t.thread_id));
                // New threads go to the top; existing threads get their data updated in place
                const newOnes = updated.filter(t => !prevIds.has(t.thread_id));
                const merged = prev
                    .filter(t => updatedMap.has(t.thread_id) || String(t.thread_id) === String(requestedThreadRef.current))
                    .map(t => updatedMap.get(t.thread_id) || t);
                return [...newOnes, ...merged];
            });
        } catch (_) { /* silent */ }
    }, [authState.status, handleSessionExpired]);

    // Apply a title pushed by the stream's `thread_title` event straight to state, so the
    // sidebar updates the moment the backend auto-generates a name — no refetch/cache race.
    // String-compares thread_id to tolerate number/string id mismatches.
    const applyThreadTitleLocal = useCallback((threadId, title) => {
        if (threadId === undefined || threadId === null || !title) return;
        setThreads(prev => prev.map(t => (
            String(t.thread_id) === String(threadId) ? { ...t, title } : t
        )));
    }, []);

    // Fetch threads when user authenticates
    useEffect(() => {
        if (authState.status === 'authenticated') {
            fetchThreads(authState.identity);
            refreshDiagnosticTickets();
            refreshGarageRequests();
        } else if (authState.status === 'anonymous') {
            setThreads([]);
            setCurrentThreadId(null);
            setRecentDiagnostics([]);
            setActiveDiagnosticTicket(null);
            setGarageRequests([]);
        }
    }, [authState.status, authState.identity, fetchThreads, refreshDiagnosticTickets, refreshGarageRequests]);

    // Refresh shop conversations whenever the driver lands on /chat (e.g. right after sending
    // an estimate from the Garage, which navigates here) so the new shop chat shows immediately.
    useEffect(() => {
        if (authState.status !== 'authenticated') return;
        if (location.pathname === '/chat' || location.pathname === '/test-chat') {
            refreshGarageRequests();
        }
    }, [location.pathname, location.key, authState.status, refreshGarageRequests]);

    useEffect(() => {
        if (authState.status !== 'authenticated' || !authState.identity || threads.length === 0) return;

        setCurrentThreadId((prev) => {
            if (prev != null && threads.some((thread) => String(thread.thread_id) === String(prev))) {
                return prev;
            }

            const storedThreadId = getStoredThreadId(authState.identity);
            if (
                storedThreadId != null &&
                threads.some((thread) => String(thread.thread_id) === String(storedThreadId))
            ) {
                return storedThreadId;
            }

            return prev;
        });
    }, [authState.status, authState.identity, threads]);

    useEffect(() => {
        const handleActiveDiagnosticTicket = (event) => {
            const detail = event?.detail;
            const nextTicket = detail && (
                getDiagnosticTicketThreadId(detail) ||
                getDiagnosticTicketRealId(detail) ||
                getDiagnosticTicketTitle(detail)
            ) ? detail : null;
            if (nextTicket) {
                const nextThreadId = getDiagnosticTicketThreadId(nextTicket);
                if (nextThreadId) {
                    setThreads((prev) => (prev || []).map((thread) => (
                        String(thread?.thread_id || '') === String(nextThreadId)
                            ? {
                                ...thread,
                                conversation_type: 'diagnosis',
                                diagnostic_ticket: mergeDiagnosticTicketForUi(thread?.diagnostic_ticket || {}, nextTicket),
                                diagnostic_ticket_id: getDiagnosticTicketRealId(nextTicket) || thread?.diagnostic_ticket_id,
                                diagnostic_ticket_status: nextTicket.status || nextTicket.ticketStatus || thread?.diagnostic_ticket_status,
                                diagnostic_ticket_stage: nextTicket.stage || nextTicket.ticketStage || thread?.diagnostic_ticket_stage,
                            }
                            : thread
                    )));
                }
                setRecentDiagnostics((prev) => {
                    if (!isDisplayableDiagnosticTicket(nextTicket)) return prev || [];
                    let matched = false;
                    const nextDiagnostics = (prev || []).map((ticket) => {
                        if (!diagnosticTicketsHaveExactIdentity(ticket, nextTicket)) return ticket;
                        matched = true;
                        return mergeDiagnosticTicketForUi(ticket, nextTicket);
                    });
                    return withPerUserTicketNumbers(matched ? nextDiagnostics : [nextTicket, ...(prev || [])]);
                });
            }
            setActiveDiagnosticTicket((prev) => {
                if (!nextTicket) return null;
                return getActiveDiagnosticTicketSignature(prev) === getActiveDiagnosticTicketSignature(nextTicket)
                    ? mergeDiagnosticTicketForUi(prev, nextTicket)
                    : nextTicket;
            });
        };

        window.addEventListener('dash_active_diagnostic_ticket', handleActiveDiagnosticTicket);
        return () => window.removeEventListener('dash_active_diagnostic_ticket', handleActiveDiagnosticTicket);
    }, []);

    // A finished report renamed the ticket (server side): show the new name in the sidebar
    // right away. The server already skipped chats the user renamed, so this only mirrors it.
    useEffect(() => {
        const handleTicketTitle = (event) => {
            const { threadId, title } = event?.detail || {};
            if (!threadId || !title) return;
            setThreads((prev) => (prev || []).map((thread) => (
                String(thread?.thread_id) === threadId && !thread?.title_locked ? { ...thread, title } : thread
            )));
            setRecentDiagnostics((prev) => (prev || []).map((ticket) => (
                String(getDiagnosticTicketThreadId(ticket) || '') === threadId ? { ...ticket, title } : ticket
            )));
        };
        window.addEventListener(TICKET_TITLE_CHANGED_EVENT, handleTicketTitle);
        return () => window.removeEventListener(TICKET_TITLE_CHANGED_EVENT, handleTicketTitle);
    }, []);

    // Chats belong to a single vehicle. When a vehicle is removed (from chat or the
    // Garage) its threads are soft-deleted server-side — refetch so they drop out of
    // the sidebar immediately, and move off the current chat if it was one of them.
    useEffect(() => {
        if (authState.status !== 'authenticated' || !authState.identity) return;
        const identity = authState.identity;
        let debounceTimer = null;
        const doRefresh = async () => {
            try {
                const res = await fetch(`${CHAT_API_BASE_URL}/threads?limit=${THREAD_HISTORY_LIMIT}`, { credentials: 'include', cache: 'no-store' });
                if (res.status === 401) { handleSessionExpired(); return; }
                // 429 = rate-limited by prod infra; skip quietly so we don't pile on.
                if (!res.ok) return;
                const data = await res.json();
                const list = data.threads || [];
                setThreads(list);
                setCurrentThreadId((cur) => {
                    if (cur == null) return cur;
                    if (list.some((thread) => String(thread.thread_id) === String(cur))) return cur;
                    // The open chat was removed along with its vehicle — move to the
                    // next remaining chat (or a fresh one if none are left).
                    const next = list[0] || null;
                    if (next && identity) setStoredThreadId(identity, next.thread_id);
                    else clearStoredThreadId(identity);
                    return next ? next.thread_id : null;
                });
            } catch (e) {
                console.error('[Threads] refresh after vehicle change failed', e);
            }
            refreshDiagnosticTickets();
        };
        // dash_vehicle_updated can fire several times in quick succession (switch +
        // update + odometer sync). Debounce so a burst triggers ONE refetch instead
        // of hammering the thread list / diagnostics endpoints (which trips the prod
        // rate limiter → 429 → "Connection interrupted").
        const handleVehiclesChanged = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(doRefresh, 1000);
        };
        window.addEventListener('dash_vehicle_updated', handleVehiclesChanged);
        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            window.removeEventListener('dash_vehicle_updated', handleVehiclesChanged);
        };
    }, [authState.status, authState.identity, refreshDiagnosticTickets, handleSessionExpired]);


    const dismissAuthPrompt = useCallback(() => {
        setAuthPromptDismissed(true);
        setAuthState((prev) => ({
            ...prev,
            promptDismissed: true,
        }));
    }, []);

    const openAuthModal = useCallback(() => {
        setIsAuthModalOpen(true);
        setAuthMode('phone');
        setAuthChannel('phone');
        setAuthStep('phone');
        setAuthError(null);
        setAuthNotice(null);
        setLoginNotice(null);
        setGuestMigrationPreview(null);
        setGuestMigrationError(null);
        resetOtpResendCooldown();
    }, [resetOtpResendCooldown]);

    const openAdminAuthModal = useCallback(() => {
        setIsAuthModalOpen(true);
        setAuthMode('admin');
        setAuthChannel('phone');
        setAuthStep('phone');
        setPhoneInput('');
        setPhoneCountry(DEFAULT_COUNTRY);
        setOtpInput('');
        setAuthError(null);
        setAuthNotice(null);
        setLoginNotice(null);
        setGuestMigrationPreview(null);
        setGuestMigrationError(null);
        resetOtpResendCooldown();
    }, [resetOtpResendCooldown]);

    const openPartnerLogin = useCallback(() => {
        window.location.assign(partnerLoginUrl());
    }, []);

    const closeAuthModal = useCallback(() => {
        setIsAuthModalOpen(false);
        setLoginNotice(null);
        setGuestMigrationPreview(null);
        setGuestMigrationError(null);
        // Reset flow state after a delay to avoid flicker
        setTimeout(() => {
            setAuthMode('phone');
            setAuthChannel('phone');
            setAuthStep('phone');
            setPhoneInput('');
            setPhoneCountry(DEFAULT_COUNTRY);
            setEmailInput('');
            setOtpInput('');
            setPendingPhone('');
            setPendingEmail('');
            setAuthError(null);
            setAuthNotice(null);
            setGuestMigrationBusy(false);
            resetOtpResendCooldown();
        }, 300);
    }, [resetOtpResendCooldown]);

    // "Tell us about you" is mandatory: any signed-in account missing a name, region,
    // phone or email gets it (right after login, or on any later visit) and cannot use
    // the app until it is filled in. It closes by itself once /auth/me reports the
    // profile complete. The partner-shop area is a separate login and is left alone.
    const isProfileSetupRequired = isProfileIncomplete(authState) && !isShopRoute;

    const completeProfileSetup = useCallback(async () => {
        await refreshAuthState();
        refreshDisplayName();
        navigate('/chat');
    }, [navigate, refreshAuthState, refreshDisplayName]);

    // Saves promo chat turns as their own conversation and opens it, so they are in the
    // sidebar and survive a reload like any other chat. Returns false if it could not.
    const openPromoConversation = useCallback(async (messages) => {
        const thread = await createThread(null, { activate: false });
        if (!thread?.thread_id) return false;
        try {
            const response = await fetch(`${CHAT_API_BASE_URL}/threads/${encodeURIComponent(thread.thread_id)}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ messages }),
            });
            if (!response.ok) return false;
        } catch {
            return false;
        }
        setActiveDiagnosticTicket(null);
        setLocalAddVehicleCardId(null);
        selectThread(thread.thread_id);
        navigate('/chat');
        return true;
    }, [createThread, navigate, selectThread]);

    // A promo code sent before logging in is redeemed once the account is signed in, its
    // profile is complete and its chats have loaded. The conversation then continues
    // where it left off: the code, the "create an account" reply, and the result.
    const promoRedeemInFlightRef = useRef(false);
    useEffect(() => {
        if (authState.status !== 'authenticated' || isAuthModalOpen || isProfileIncomplete(authState)) return;
        if (threadsLoadedFor !== authState.identity) return;
        const code = getPendingPromoCode();
        if (!code || promoRedeemInFlightRef.current) return;
        promoRedeemInFlightRef.current = true;

        (async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/promo/redeem`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ code }),
                });
                const data = await parseJsonIfPresent(response);
                // Network/server trouble or a not-ready account: keep the code and retry later.
                if (!response.ok || !data?.status || ['login_required', 'profile_incomplete'].includes(data.status)) return;
                clearPendingPromoCode();
                if (data.status === 'not_promo') return;
                await openPromoConversation([
                    { role: 'user', content: code, message_type: 'chat' },
                    { role: 'assistant', content: promoReplyText({ ...data, status: 'login_required' }), message_type: 'chat' },
                    { role: 'assistant', content: promoReplyText(data), message_type: 'chat' },
                ]);
            } catch (err) {
                console.warn('Promo redeem after login failed:', err);
            } finally {
                promoRedeemInFlightRef.current = false;
            }
        })();
    }, [authState, isAuthModalOpen, openPromoConversation, threadsLoadedFor]);

    // Set while signing out of the shop, so the guard below reads a deliberate
    // logout as such instead of as an unauthorised visit.
    const adminLoggingOutRef = useRef(false);

    useEffect(() => {
        // Guards /shop only. Accepts either the new partner-shop session or the
        // old internal admin session during migration.
        if (adminAuthState.status === 'loading') return;
        if (!(location.pathname === '/shop' || location.pathname.startsWith('/shop/'))) {
            adminLoggingOutRef.current = false;
            return;
        }
        if (adminAuthState.status !== 'authenticated' && !adminLoggingOutRef.current) {
            openAdminAuthModal();
        }
    }, [adminAuthState.status, location.pathname, openAdminAuthModal]);

    // value = full phone string from react-phone-input-2 (dialCode + nationalDigits, no '+')
    const handlePhoneInputChange = useCallback((value, country) => {
        // Store full value directly — no slicing needed
        setPhoneInput(String(value || '').replace(/\D/g, ''));
        if (country?.dialCode) setPhoneCountry(country);
        if (authError) setAuthError(null);
    }, [authError]);

    const handleRequestOtp = async () => {
        // phoneInput is already the full value (dialCode + national digits) from the library
        const validationError = getPhoneValidationError(phoneInput, phoneCountry);
        if (validationError) {
            setAuthError(validationError);
            return;
        }

        setAuthBusy(true);
        setAuthError(null);
        setAuthNotice(null);

        // normalizePhoneForOtp ensures correct format for backend
        const targetPhone = normalizePhoneForOtp(phoneInput, phoneCountry);

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone: targetPhone }),
            });

            const data = await parseJsonIfPresent(response);
            if (!response.ok) throw new Error(data?.detail || data?.error || 'Failed to send OTP.');

            setPendingPhone(targetPhone);
            setAuthStep('otp');
            startOtpResendCooldown();
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const handleEmailInputChange = useCallback((value) => {
        setEmailInput(value);
        if (authError) setAuthError(null);
    }, [authError]);

    const handleSelectAuthChannel = useCallback((channel) => {
        setAuthChannel(channel === 'email' ? 'email' : 'phone');
        setAuthError(null);
        setAuthNotice(null);
    }, []);

    const handleRequestEmailOtp = async () => {
        const validationError = getEmailValidationError(emailInput);
        if (validationError) {
            setAuthError(validationError);
            return;
        }

        setAuthBusy(true);
        setAuthError(null);
        setAuthNotice(null);

        const targetEmail = normalizeEmail(emailInput);

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/request-email-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: targetEmail }),
            });

            const data = await parseJsonIfPresent(response);
            if (!response.ok) {
                throw new Error(getEmailAuthErrorMessage(response, data, 'Failed to send code.'));
            }

            setPendingEmail(targetEmail);
            setAuthChannel('email');
            setAuthStep('otp');
            startOtpResendCooldown();
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const handleResendOtp = async () => {
        if (isOtpResendCoolingDown) return;

        setAuthBusy(true);
        setAuthError(null);
        setAuthNotice(null);

        const isEmailChannel = authChannel === 'email';
        const endpoint = isEmailChannel ? 'request-email-otp' : 'request-otp';
        const body = isEmailChannel ? { email: pendingEmail } : { phone: pendingPhone };

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });

            const data = await parseJsonIfPresent(response);
            if (!response.ok) {
                throw new Error(
                    isEmailChannel
                        ? getEmailAuthErrorMessage(response, data, 'Failed to send code.')
                        : (data?.detail || data?.error || 'Failed to send OTP.')
                );
            }

            setAuthNotice('New one time code sent to');
            startOtpResendCooldown();
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const holdLoginForGuestMigration = useCallback((data) => {
        if (!data?.anonymous_migration_available || !data?.anonymous_migration_preview) {
            return false;
        }
        setGuestMigrationPreview(data.anonymous_migration_preview);
        setGuestMigrationError(null);
        setAuthNotice(null);
        return true;
    }, []);

    // #739: the code was right but this user has not accepted the current consent version.
    // No session exists yet; show both boxes with the required one unchecked and the
    // optional marketing box unchecked, including when renewing older consent.
    const startLoginConsentStep = (data) => {
        if (!isLoginConsentRequired(data)) return false;
        setServiceConsentAccepted(false);
        setMarketingConsentOptIn(false);
        setLoginConsentRequest({ token: data.consent_token, reason: data.consent_reason || 'missing', verifyData: data });
        setOtpInput('');
        setAuthStep('consent');
        return true;
    };

    const handleAcceptLoginConsent = async () => {
        if (!loginConsentRequest?.token || !serviceConsentAccepted || authBusy) return;
        setAuthBusy(true);
        setAuthError(null);
        try {
            await acceptLoginConsent(AUTH_API_BASE_URL, {
                consentToken: loginConsentRequest.token,
                consent: getLoginConsentPayload(),
            });

            await refreshAuthState();
            if (!holdLoginForGuestMigration(loginConsentRequest.verifyData)) {
                closeAuthModal();
            }
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const handleVerifyOtp = async () => {
        const otp = normalizeOtpInput(otpInput);

        if (otp.length !== OTP_LENGTH) {
            setAuthError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setAuthBusy(true);
        setAuthError(null);

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone: pendingPhone,
                    otp,
                    web_id: localStorage.getItem('dash_web_user_id') || '',
                }),
            });

            const data = await parseJsonIfPresent(response);
            if (!response.ok) throw new Error(data?.detail || data?.error || 'Invalid code.');
            if (startLoginConsentStep(data)) return;

            // Success!
            await refreshAuthState();
            if (!holdLoginForGuestMigration(data)) {
                closeAuthModal();
            }
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const handleVerifyEmailOtp = async () => {
        const otp = normalizeOtpInput(otpInput);

        if (otp.length !== OTP_LENGTH) {
            setAuthError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setAuthBusy(true);
        setAuthError(null);

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/verify-email-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    email: pendingEmail,
                    otp,
                    web_id: localStorage.getItem('dash_web_user_id') || '',
                }),
            });

            const data = await parseJsonIfPresent(response);
            if (!response.ok) {
                throw new Error(getEmailAuthErrorMessage(response, data, 'Invalid code.'));
            }
            if (startLoginConsentStep(data)) return;

            await refreshAuthState();
            if (!holdLoginForGuestMigration(data)) {
                closeAuthModal();
            }
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const handleConfirmGuestMigration = useCallback(async () => {
        const webId = localStorage.getItem('dash_web_user_id') || '';
        if (!webId) {
            setGuestMigrationPreview(null);
            closeAuthModal();
            return;
        }

        setGuestMigrationBusy(true);
        setGuestMigrationError(null);

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/claim-anonymous-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ web_id: webId }),
            });
            const data = await parseJsonIfPresent(response);
            if (response.status === 401) {
                handleSessionExpired();
                return;
            }
            if (!response.ok) {
                throw new Error(data?.detail || data?.error || 'Unable to move guest data.');
            }

            localStorage.removeItem('dash_web_user_id');
            localStorage.removeItem('dash_web_session_id');
            localStorage.removeItem('dash_web_session_last_activity');
            setGuestMigrationPreview(null);
            await refreshAuthState();
            const nextIdentity = authState.identity || pendingPhone || pendingEmail;
            if (nextIdentity) {
                await fetchThreads(nextIdentity);
            }
            await refreshDiagnosticTickets();
            closeAuthModal();
        } catch (err) {
            setGuestMigrationError(err.message || 'Unable to move guest data.');
        } finally {
            setGuestMigrationBusy(false);
        }
    }, [authState.identity, closeAuthModal, fetchThreads, handleSessionExpired, pendingEmail, pendingPhone, refreshAuthState, refreshDiagnosticTickets]);

    const handleSkipGuestMigration = useCallback(() => {
        setGuestMigrationPreview(null);
        setGuestMigrationError(null);
        closeAuthModal();
    }, [closeAuthModal]);

    const handleVerifyAdmin = async () => {
        const validationError = getPhoneValidationError(phoneInput, phoneCountry);
        if (validationError) {
            setAuthError(validationError);
            return;
        }

        const otp = normalizeOtpInput(otpInput);
        if (otp.length !== OTP_LENGTH) {
            setAuthError(`Please enter the ${OTP_LENGTH}-digit admin code.`);
            return;
        }

        setAuthBusy(true);
        setAuthError(null);
        setAuthNotice(null);

        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/verify-admin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone: normalizePhoneForOtp(phoneInput, phoneCountry),
                    otp,
                }),
            });

            const data = await parseJsonIfPresent(response);
            if (!response.ok) throw new Error(data?.detail || data?.error || 'Invalid admin credentials.');

            await refreshAdminAuthState();
            closeAuthModal();
            navigate('/shop', { replace: true });
        } catch (err) {
            setAuthError(err.message);
        } finally {
            setAuthBusy(false);
        }
    };

    const toggleSidebar = useCallback(() => {
        setIsSidebarOpen(prev => !prev);
    }, []);

    const toggleCollapse = useCallback(() => {
        setIsSidebarCollapsed(prev => !prev);
    }, []);

    const closeSidebar = useCallback(() => {
        setIsSidebarOpen(false);
    }, []);

    // The two identities log out independently: signing out of the shop leaves the
    // driver's chat session untouched, and vice versa.
    const handleLogout = useCallback(async () => {
        if (location.pathname === '/shop') {
            // Revokes the shop/admin portal cookies only. The driver's session_id
            // is left alone, so a signed-in driver keeps their chat separately.
            try {
                await Promise.allSettled([
                    fetch(`${getApiBaseUrl()}/shop-auth/logout`, {
                        method: 'POST',
                        credentials: 'include',
                    }),
                    fetch(`${AUTH_API_BASE_URL}/admin/logout`, {
                        method: 'POST',
                        credentials: 'include',
                    }),
                ]);
            } catch (error) {
                console.error('Failed to log out shop session:', error);
            } finally {
                adminLoggingOutRef.current = false;
                setIsAuthModalOpen(false);
                setAdminAuthState({ status: 'anonymous', authenticated: false });
                navigate('/shop', { replace: true });
                openAdminAuthModal();
            }
            return;
        }

        try {
            await fetch(`${AUTH_API_BASE_URL}/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch (error) {
            console.error('Failed to log out session:', error);
        } finally {
            if (authState.identity) clearStoredThreadId(authState.identity);
            setThreads([]);
            setCurrentThreadId(null);
            setThreadsLoading(false);
            setRecentDiagnostics([]);
            setDiagnosticsLoading(false);
            setActiveDiagnosticTicket(null);
            setDisplayName('');
            setAvatarUrl('');
            setAuthState((prev) => ({
                ...prev,
                status: 'anonymous',
                phone: '',
            }));
            setIsAuthModalOpen(false);
            notifyChatReset('logout', { persist: false });
        }
    }, [authState.identity, location.pathname, navigate, openAdminAuthModal]);

    // Conversational deletion (agent `reset_user_memory` -> SSE `account_deleted`)
    // resets the chat in-place but not the sidebar's auth state. Mirror the logout
    // teardown so the user isn't left appearing signed-in after their data is wiped.
    useEffect(() => {
        const handleAccountDeleted = () => { handleLogout(); };
        window.addEventListener('dash_account_deleted', handleAccountDeleted);
        return () => window.removeEventListener('dash_account_deleted', handleAccountDeleted);
    }, [handleLogout]);

    const shouldShowPhonePrompt = authState.status === 'anonymous' && !authState.promptDismissed;
    // The active sidebar/thread views must never show a conversation archived this session,
    // even if a server refetch momentarily lags behind the archive.
    const visibleThreads = locallyArchivedThreadIds.size === 0
        ? threads
        : threads.filter((t) => !locallyArchivedThreadIds.has(String(t.thread_id)));
    const linkedRecentDiagnostics = attachDiagnosticThreadIds(recentDiagnostics, threads)
        // Hide diagnosis cards for conversations archived this session, even if a tickets
        // refresh re-includes them (the thread list already excludes archived server-side).
        .filter((ticket) => {
            if (locallyArchivedThreadIds.size === 0) return true;
            return ![ticket?.thread_id, ticket?.source_repair_log_id, ticket?.repair_log_id, ticket?.source_session_id]
                .some((id) => id != null && locallyArchivedThreadIds.has(String(id)));
        });

    return (
        <div className="flex flex-row h-[100dvh] overflow-hidden bg-gradient-to-br from-[#F9F9F9] via-[#F7F7F7] to-[#F2F2F2] relative">
            <MobileAppNav authState={authState} onMenuClick={toggleSidebar} isAdminView={isShopRoute} notificationsUnread={notificationsUnread} />

            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div 
                    className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-[55] transition-opacity duration-300"
                    onClick={closeSidebar}
                />
            )}

            <ChatSidebar
                authState={authState}
                adminAuthState={adminAuthState}
                displayName={displayName}
                avatarUrl={avatarUrl}
                onOpenAuthModal={openAuthModal}
                onLogout={handleLogout}
                isOpen={isSidebarOpen}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={toggleCollapse}
                onClose={closeSidebar}
                onOpenHelp={() => setIsHelpOpen(true)}
                // onOpenOnboarding={() => setShowOnboarding(true)} // #711
                threads={visibleThreads}
                threadsLoading={threadsLoading}
                currentThreadId={currentThreadId}
                addVehicleThreadIds={addVehicleThreadIds}
                onNewThread={startNewChat}
                onSelectThread={selectThread}
                onDeleteThread={deleteThread}
                onRenameThread={renameThread}
                onArchiveThread={archiveThread}
                onRestoreThread={restoreThread}
                fetchArchivedThreads={fetchArchivedThreads}
                notificationsUnread={notificationsUnread}
                recentDiagnostics={linkedRecentDiagnostics}
                activeDiagnosticTicket={activeDiagnosticTicket}
                diagnosticsLoading={diagnosticsLoading}
                garageRequests={garageRequests}
            />
            <main className={`flex-1 min-h-0 overflow-hidden relative flex flex-col pt-14 transition-all duration-300 md:pt-0 md:pb-0 ${isShopRoute ? 'pb-[env(safe-area-inset-bottom)]' : 'pb-[calc(56px+env(safe-area-inset-bottom))]'}`}>
                {/* Phone link prompt — ChatPage only */}
                {isChatPage && shouldShowPhonePrompt && (
                    <div className="px-2.5 pt-2.5 md:px-4 md:pt-4 max-w-5xl mx-auto w-full z-40 flex-shrink-0">
                        <PhoneLinkPrompt
                            onOpenAuthModal={openAuthModal}
                            onDismiss={dismissAuthPrompt}
                        />
                    </div>
                )}
                
                <div className="flex-1 min-h-0 overflow-hidden relative">
                    {!selectionReady ? (
                        <div className="p-6 text-sm font-semibold" role="status">
                            {threadSelection?.status === 'error'
                                && String(threadSelection.threadId) === String(requestedThreadId)
                                ? threadSelection.error
                                : authState.status === 'anonymous'
                                    ? <button type="button" onClick={openAuthModal}>Sign in to continue this chat</button>
                                    : 'Opening your referral chat...'}
                        </div>
                    ) : <Outlet
                        key={`${location.pathname}${location.search}`}
                        context={{
                            authState,
                            refreshAuthState,
                            dismissAuthPrompt,
                            isAuthModalOpen,
                            openAuthModal,
                            closeAuthModal,
                            onLogout: handleLogout,
                            setPromptDismissed: setAuthPromptDismissed,
                            pendingQuestion,
                            clearPendingQuestion: () => setPendingQuestion(null),
                            // Send a question to CarFido from anywhere (e.g. the Garage
                            // "Ask CarFido" action on a maintenance item). Pass { newChat: true }
                            // to open a FRESH chat first (so it never lands in whatever
                            // conversation the user currently has open).
                            //
                            // For newChat we reset to a fresh chat (currentThreadId = null) and
                            // hand the question to ChatPage via navigation state `q`. ChatPage's
                            // init effect consumes `q` INSIDE its normal fresh-chat flow — the
                            // same race-free path the parts/shop redirects use — which creates a
                            // server-persisted thread AND resets the message list. Using a
                            // separate pendingQuestion effect here instead raced with that init
                            // flow, sometimes dropping the new chat and sometimes leaking the
                            // previous chat's messages into it.
                            askDash: (question, options = {}) => {
                                if (!question) return;
                                if (options?.newChat) {
                                    setCurrentThreadId(null);
                                    setActiveDiagnosticTicket(null);
                                    if (authState.phone) clearStoredThreadId(authState.phone);
                                    notifyChatReset('fresh-chat');
                                    navigate('/chat', { replace: true, state: { q: question } });
                                    return;
                                }
                                if (location.pathname !== '/chat') navigate('/chat');
                                setPendingQuestion(question);
                            },
                            // Thread management
                            threads: visibleThreads,
                            threadsLoading,
                            currentThreadId,
                            onNewThread: createThread,
                            onStartNewChat: startNewChat,
                            onSelectThread: selectThread,
                            onDeleteThread: deleteThread,
                            onRenameThread: renameThread,
                            onArchiveThread: archiveThread,
                            onRestoreThread: restoreThread,
                            fetchArchivedThreads,
                            notifications,
                            notificationsUnread,
                            markNotificationRead,
                            markAllNotificationsRead,
                            dismissNotification,
                            refreshNotifications,
                            addVehicleThreadIds,
                            localAddVehicleCardId,
                            refreshThreadTitles,
                            applyThreadTitleLocal,
                            recentDiagnostics: linkedRecentDiagnostics,
                            activeDiagnosticTicket,
                            diagnosticsLoading,
                            refreshDiagnosticTickets,
                            displayName,
                            refreshDisplayName,
                            avatarUrl,
                            setAvatarUrl,
                            openPromoConversation,
                        }}
                    />}
                </div>
            </main>

            <PhoneAuthModal
                isOpen={isAuthModalOpen}
                authMode={authMode}
                authChannel={authChannel}
                authStep={authStep}
                phoneInput={phoneInput}
                phoneCountry={phoneCountry}
                emailInput={emailInput}
                otpInput={otpInput}
                pendingPhone={pendingPhone}
                pendingEmail={pendingEmail}
                authError={authError}
                authNotice={authNotice}
                loginNotice={loginNotice}
                authBusy={authBusy}
                serviceConsentAccepted={serviceConsentAccepted}
                marketingConsentOptIn={marketingConsentOptIn}
                consentReason={loginConsentRequest?.reason || ''}
                onAcceptLoginConsent={handleAcceptLoginConsent}
                onServiceConsentChange={setServiceConsentAccepted}
                onMarketingConsentChange={setMarketingConsentOptIn}
                onClose={closeAuthModal}
                onPhoneInputChange={handlePhoneInputChange}
                onEmailInputChange={handleEmailInputChange}
                onOtpInputChange={(value) => setOtpInput(normalizeOtpInput(value))}
                onSelectChannel={handleSelectAuthChannel}
                onRequestOtp={handleRequestOtp}
                onRequestEmailOtp={handleRequestEmailOtp}
                onVerifyOtp={authChannel === 'email' ? handleVerifyEmailOtp : handleVerifyOtp}
                onVerifyAdmin={handleVerifyAdmin}
                onResendOtp={handleResendOtp}
                resendSecondsRemaining={otpResendSecondsRemaining}
                onResetPhoneStep={() => {
                    setAuthStep('phone');
                    setOtpInput('');
                    setAuthError(null);
                    setAuthNotice(null);
                    resetOtpResendCooldown();
                }}
                onSwitchToAdmin={openPartnerLogin}
                onSwitchToPhone={() => {
                    setAuthMode('phone');
                    setAuthChannel('phone');
                    setAuthStep('phone');
                    setOtpInput('');
                    setAuthError(null);
                    setAuthNotice(null);
                    resetOtpResendCooldown();
                }}
            />

            <ProfileSetupModal
                isOpen={isProfileSetupRequired && !isAuthModalOpen}
                authState={authState}
                onLogout={handleLogout}
                onComplete={completeProfileSetup}
                onSessionExpired={handleSessionExpired}
            />

            {isAuthModalOpen && guestMigrationPreview && (
                <GuestDataMigrationPrompt
                    preview={guestMigrationPreview}
                    busy={guestMigrationBusy}
                    error={guestMigrationError}
                    onConfirm={handleConfirmGuestMigration}
                    onSkip={handleSkipGuestMigration}
                />
            )}

            <HelpSupportModal
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                onSelectQuestion={(question) => {
                    if (location.pathname !== '/chat') {
                        navigate('/chat');
                    }
                    setPendingQuestion(question);
                    setIsHelpOpen(false);
                }}
            />

            {!isShopRoute && <PwaInstallPrompt />}

            {/* #711: infographic intro disabled
            {showOnboarding && (
                <OnboardingOverlay onDismiss={() => setShowOnboarding(false)} />
            )}
            */}
        </div>
    );
};

export default Layout;
